import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import React, { useCallback, useRef, useState } from 'react'

import ImageService from '../../services/ImageService'

/**
 * 可重用的图片上传组件
 * 支持拖放和文件选择
 *
 * @param {Object} props
 * @param {Function} props.onUploadSuccess - 上传成功回调，接收 (imageData) => void
 * @param {Function} [props.onUploadError] - 上传错误回调，接收 (error) => void
 * @param {Function} [props.customUploadHandler] - 自定义上传处理函数，接收 (file) => Promise<imageData>，如果提供则使用此函数而不是默认上传
 * @param {string} [props.accept] - 接受的文件类型，默认为 'image/jpeg,image/png,image/gif,image/webp'
 * @param {number} [props.maxSize] - 最大文件大小（字节），默认为 10MB
 * @param {boolean} [props.showPreview] - 是否显示预览，默认为 false
 * @param {string} [props.buttonText] - 按钮文本，默认为 'Upload Image'
 * @param {string} [props.dragText] - 拖放区域文本，默认为 'Drag and drop an image here, or click to select'
 * @param {Object} [props.sx] - 额外的样式
 * @param {boolean} [props.disabled] - 是否禁用
 * @param {React.ReactNode} [props.children] - 自定义内容（如果提供，将替换默认的拖放区域）
 */
const ImageUploader = ({
  onUploadSuccess,
  onUploadError,
  customUploadHandler,
  accept = 'image/jpeg,image/png,image/gif,image/webp',
  maxSize = 10 * 1024 * 1024, // 10MB
  showPreview = false,
  buttonText = 'Upload Image',
  dragText = 'Drag and drop an image here, or click to select',
  sx = {},
  disabled = false,
  children,
}) => {
  const fileInputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [previewUrl, setPreviewUrl] = useState(null)

  /**
   * 验证文件
   */
  const validateFile = (file) => {
    // 验证文件类型
    const allowedTypes = accept.split(',').map((type) => type.trim())
    if (!allowedTypes.includes(file.type)) {
      const allowedExtensions = allowedTypes.map((type) => type.split('/')[1].toUpperCase()).join(', ')
      throw new Error(`Invalid file type. Only ${allowedExtensions} are allowed.`)
    }

    // 验证文件大小
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0)
      throw new Error(`File size exceeds ${maxSizeMB}MB limit.`)
    }

    return true
  }

  /**
   * 处理文件上传
   */
  const handleFileUpload = useCallback(
    async (file) => {
      if (!file) return

      try {
        // 验证文件
        validateFile(file)

        setIsUploading(true)
        setUploadError('')
        setPreviewUrl(null)

        // 创建预览 URL（如果启用预览）
        if (showPreview) {
          const url = URL.createObjectURL(file)
          setPreviewUrl(url)
        }

        // 上传图片到服务器（使用自定义处理函数或默认上传）
        const imageData = customUploadHandler ? await customUploadHandler(file) : await ImageService.uploadImage(file)

        // 调用成功回调
        if (onUploadSuccess) {
          onUploadSuccess(imageData)
        }

        // 清理预览 URL
        if (showPreview && previewUrl) {
          URL.revokeObjectURL(previewUrl)
        }
      } catch (error) {
        console.error('Failed to upload image:', error)
        const errorMessage = error.message || 'Failed to upload image. Please try again.'
        setUploadError(errorMessage)

        // 调用错误回调
        if (onUploadError) {
          onUploadError(error)
        }
      } finally {
        setIsUploading(false)
      }
    },
    [accept, maxSize, showPreview, onUploadSuccess, onUploadError, customUploadHandler, previewUrl],
  )

  /**
   * 处理文件选择
   */
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
    // 重置文件输入，允许选择同一个文件
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  /**
   * 处理打开文件选择对话框
   */
  const handleOpenFileDialog = () => {
    if (disabled || isUploading) return
    setUploadError('')
    fileInputRef.current?.click()
  }

  /**
   * 处理拖放事件
   */
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled || isUploading) return

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  // 清理预览 URL
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  // 默认的拖放区域内容
  const defaultDropZone = (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleOpenFileDialog}
      sx={{
        border: '2px dashed',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        transition: 'all 0.2s ease-in-out',
        opacity: disabled || isUploading ? 0.6 : 1,
        '&:hover': {
          borderColor: disabled || isUploading ? 'divider' : 'primary.main',
          backgroundColor: disabled || isUploading ? 'transparent' : 'action.hover',
        },
        ...sx,
      }}
    >
      {isUploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Uploading...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography variant="body1" color="text.secondary">
            {dragText}
          </Typography>
          <Button variant="outlined" size="small" disabled={disabled}>
            {buttonText}
          </Button>
        </Box>
      )}
      {showPreview && previewUrl && (
        <Box sx={{ mt: 2 }}>
          <img
            src={previewUrl}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              borderRadius: '4px',
            }}
          />
        </Box>
      )}
    </Box>
  )

  return (
    <>
      {/* 隐藏的文件输入 */}
      <input ref={fileInputRef} type="file" accept={accept} style={{ display: 'none' }} onChange={handleFileSelect} disabled={disabled || isUploading} />

      {/* 自定义内容或默认拖放区域 */}
      {children ? (
        <Box onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} sx={{ ...sx }}>
          {children}
        </Box>
      ) : (
        defaultDropZone
      )}

      {/* 错误提示对话框 */}
      <Dialog open={!!uploadError} onClose={() => setUploadError('')} maxWidth="sm" fullWidth>
        <DialogTitle>Image Upload Error</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            value={uploadError}
            error
            InputProps={{
              readOnly: true,
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadError('')} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ImageUploader
